import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  BarChart3,
  Factory,
  FileText,
  Link2,
  MessageCircle,
  Package,
  ShoppingCart,
  Store,
  TrendingUp,
  Truck,
} from 'lucide-react';
import PublicSiteShell, { Reveal, registroHref, whatsappHref } from '../components/public/PublicSiteShell';
import { usePublicPlans } from '../hooks/usePublicPlans';

const demoHref = `${whatsappHref.split('?')[0]}?text=Hola%2C%20quiero%20coordinar%20una%20demo%20de%20Gastify`;

export default function Funcionalidades() {
  // Los dias de prueba salen de app_settings (usePublicPlans), no hardcodeados:
  // asi la landing, precios, el registro y esta pagina dicen siempre lo mismo.
  const { trialDays } = usePublicPlans();
  return (
    <PublicSiteShell>
      <div className="gst-container">
        <Reveal className="pg-header" as="header">
          <div className="gst-eyebrow gst-eyebrow-mint">Funcionalidades</div>
          <h1>Todo lo que tu negocio necesita, en un mismo lenguaje de control.</h1>
          <p>
            Ventas, compras, stock y producción conectados en un mismo sistema, para que cada
            decisión se apoye en datos reales y no en cálculos hechos a mano.
          </p>
        </Reveal>
      </div>

      <section className="stat-strip">
        <div className="gst-container stat-strip-inner">
          <div className="stat-strip-item"><strong>{trialDays} días</strong> de prueba gratis, sin tarjeta</div>
          <div className="stat-strip-item">Facturación <strong>AFIP/ARCA</strong> incluida</div>
          <div className="stat-strip-item"><strong>Sin permanencia</strong> mínima: cambiás de plan cuando quieras</div>
        </div>
      </section>

      <div className="gst-container">
        <section className="demo-cta">
          <Reveal className="demo-card">
            <div>
              <div className="gst-eyebrow gst-eyebrow-mint">Demo en vivo</div>
              <h2>¿Querés ver a Gastify funcionando con tus propios números?</h2>
              <p>
                Coordinamos una demostración de 20 minutos por WhatsApp, donde te mostramos el
                sistema aplicado a tu tipo de negocio.
              </p>
            </div>
            <a className="gst-btn gst-btn-dark" href={demoHref} target="_blank" rel="noopener noreferrer">
              <MessageCircle size={16} /> Quiero una demo
            </a>
          </Reveal>
        </section>
      </div>

      <section className="gst-section-alt">
        <div className="gst-container" style={{ padding: '56px 0' }}>
          <div className="pillars-list">
            <Reveal className="pillar-row">
              <div className="pillar-text">
                <div className="icon-badge"><ShoppingCart size={22} /></div>
                <h3>Ventas</h3>
                <p>Cobrá rápido y con control desde el mostrador o varios puntos de venta a la vez, con facturación AFIP/ARCA incluida.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/ventas" style={{ marginTop: 20 }}>
                  Ver el módulo <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head"><span className="t">Ventas</span></div>
                <div className="pmock-kpis">
                  <div className="pmock-kpi"><div className="l">Cantidad</div><div className="v">24</div></div>
                  <div className="pmock-kpi"><div className="l">Cobrado</div><div className="v">$56.290</div></div>
                </div>
                <div className="pmock-tablerow">
                  <span className="name">Almacén Norte</span>
                  <span className="pmock-tag" style={{ background: 'var(--gst-mint-100)', color: 'var(--gst-mint-700)' }}>Cobrado</span>
                </div>
                <div className="pmock-tablerow">
                  <span className="name">Kiosco Delta</span>
                  <span className="pmock-tag" style={{ background: '#FEE2E2', color: '#991B1B' }}>Vencido</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row rev">
              <div className="pillar-text">
                <div className="icon-badge"><Truck size={22} /></div>
                <h3>Compras</h3>
                <p>Ordená proveedores y reposición sin perder el hilo entre pedido, factura y pago.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/compras" style={{ marginTop: 20 }}>
                  Ver el módulo <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head"><span className="t">Compras</span></div>
                <div className="pmock-kpis">
                  <div className="pmock-kpi"><div className="l">Cantidad</div><div className="v">28</div></div>
                  <div className="pmock-kpi"><div className="l">Pagado</div><div className="v">$123.493</div></div>
                </div>
                <div className="pmock-tablerow">
                  <span className="name">Insumos del Litoral</span>
                  <span className="pmock-tag" style={{ background: 'var(--gst-mint-100)', color: 'var(--gst-mint-700)' }}>Pagado</span>
                </div>
                <div className="pmock-tablerow">
                  <span className="name">Logística Cuyo</span>
                  <span className="pmock-tag" style={{ background: 'var(--gst-sand)', color: '#78350F' }}>A pagar</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row">
              <div className="pillar-text">
                <div className="icon-badge"><Package size={22} /></div>
                <h3>Stock</h3>
                <p>Cada movimiento impacta en tiempo real para que dejes de reponer a ciegas.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/stock" style={{ marginTop: 20 }}>
                  Ver el módulo <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock" style={{ position: 'relative' }}>
                <div className="pmock-badge-float">+3 depósitos</div>
                <div className="pmock-head"><span className="t">Stock en tiempo real</span></div>
                <div className="pmock-stockrow">
                  <span className="dot" style={{ background: 'var(--gst-mint-500)' }} />
                  <span className="name">Harina 000 x 25kg</span>
                  <span className="pmock-stockbar"><div style={{ width: '82%', background: 'var(--gst-mint-500)' }} /></span>
                  <span className="pmock-qty">128 u</span>
                </div>
                <div className="pmock-stockrow">
                  <span className="dot" style={{ background: '#DC2626' }} />
                  <span className="name">Aceite de girasol</span>
                  <span className="pmock-stockbar"><div style={{ width: '12%', background: '#DC2626' }} /></span>
                  <span className="pmock-qty">6 u</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row rev">
              <div className="pillar-text">
                <div className="icon-badge"><Factory size={22} /></div>
                <h3>Producción</h3>
                <p>Recetas y costos reales para negocios que fabrican lo que venden.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/produccion" style={{ marginTop: 20 }}>
                  Ver el módulo <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock" style={{ position: 'relative' }}>
                <div className="pmock-badge-float">$274,50 / unidad</div>
                <div className="pmock-head"><span className="t">Receta · Pan de campo x 40u</span></div>
                <div className="pmock-introw">
                  <span className="chip"><Package size={15} /></span>
                  <span className="name">Harina 000 · 12 kg</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gst-graphite-700)' }}>$9.600</span>
                </div>
                <div className="pmock-introw">
                  <span className="chip"><Package size={15} /></span>
                  <span className="name">Levadura · 300 g</span>
                  <span style={{ fontSize: 12, fontWeight: 800, color: 'var(--gst-graphite-700)' }}>$1.200</span>
                </div>
              </div>
            </Reveal>

            <Reveal className="pillar-row">
              <div className="pillar-text">
                <div className="icon-badge"><BarChart3 size={22} /></div>
                <h3>Análisis y rentabilidad</h3>
                <p>Cuando tu negocio necesita más profundidad, sumás dirección con datos reales.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/analisis" style={{ marginTop: 20 }}>
                  Ver el módulo <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head">
                  <span className="t">Ranking de productos</span>
                  <TrendingUp size={16} style={{ color: 'var(--gst-mint-600)' }} />
                </div>
                <div className="pmock-rank"><span className="pmock-medal">1</span><span className="nm">Combo desayuno</span><span className="pct">+18%</span></div>
                <div className="pmock-rank"><span className="pmock-medal">2</span><span className="nm">Pan de campo</span><span className="pct">+12%</span></div>
              </div>
            </Reveal>

            <Reveal className="pillar-row rev">
              <div className="pillar-text">
                <div className="icon-badge"><Link2 size={22} /></div>
                <h3>Integraciones</h3>
                <p>Conectá AFIP/ARCA, Mercado Libre y Tiendanube para que la información viaje sola entre plataformas.</p>
                <Link className="gst-btn gst-btn-ghost gst-btn-sm" to="/funcionalidades/integraciones" style={{ marginTop: 20 }}>
                  Ver el módulo <ArrowRight size={14} />
                </Link>
              </div>
              <div className="pillar-mock">
                <div className="pmock-head"><span className="t">Integraciones</span></div>
                <div className="pmock-introw"><span className="chip"><FileText size={15} /></span><span className="name">Facturación AFIP/ARCA</span><div className="pmock-toggle" /></div>
                <div className="pmock-introw"><span className="chip"><ShoppingCart size={15} /></span><span className="name">Mercado Libre</span><div className="pmock-toggle" /></div>
                <div className="pmock-introw"><span className="chip"><Store size={15} /></span><span className="name">Tiendanube</span><div className="pmock-toggle" /></div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </PublicSiteShell>
  );
}
